import StudentService from '../services/StudentService';
import SafeguardingService from '../services/SafeguardingService';
import AttendanceService from '../services/AttendanceService';
import DocumentService from '../services/DocumentService';
import AuditService from '../services/AuditService';

export default class StudentController {
  constructor({ repository, auditLogger }) {
    this.studentService = new StudentService(repository, auditLogger);
    this.safeguardingService = new SafeguardingService(this.studentService);
    this.attendanceService = new AttendanceService(this.studentService);
    this.documentService = new DocumentService(this.studentService);
    this.auditService = new AuditService(auditLogger);
    this.listeners = new Set();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notify() {
    this.listeners.forEach((listener) => listener());
  }

  listStudents() {
    return this.studentService.list();
  }

  getStudent(id) {
    return this.studentService.getById(id);
  }

  createStudent(data, user) {
    const student = this.studentService.create(data, user);
    this.notify();
    return student;
  }

  updateStudent(id, data, user) {
    const student = this.studentService.update(id, data, user);
    this.notify();
    return student;
  }

  setStatus(id, status, user) {
    const student = this.studentService.setStatus(id, status, user);
    this.notify();
    return student;
  }

  bulkAssignKeyWorker(ids, keyWorker, user) {
    ids.forEach((id) => {
      this.studentService.update(id, { keyWorker, lastUpdated: new Date().toISOString() }, user);
    });
    this.notify();
  }

  bulkArchive(ids, user) {
    ids.forEach((id) => {
      this.studentService.setStatus(id, 'Archived', user);
    });
    this.notify();
  }

  bulkFlagReview(ids, user) {
    ids.forEach((id) => {
      this.studentService.setStatus(id, 'Under Review', user);
    });
    this.notify();
  }

  exportStudentsCsv(students) {
    return this.studentService.buildCsv(students);
  }

  buildMetrics(students, attendanceThreshold) {
    return this.studentService.buildMetrics(students, attendanceThreshold);
  }

  groupByKeyWorker(students) {
    return this.studentService.groupByKeyWorker(students);
  }

  addSafeguardingEvent(id, data, user) {
    const student = this.safeguardingService.addEvent(id, data, user);
    this.notify();
    return student;
  }

  addMedicalEvent(id, data, user) {
    const student = this.safeguardingService.addMedicalEvent(id, data, user);
    this.notify();
    return student;
  }

  addPoliceEvent(id, data, user) {
    const student = this.safeguardingService.addPoliceEvent(id, data, user);
    this.notify();
    return student;
  }

  addDocument(id, data, user) {
    const student = this.documentService.addDocument(id, data, user);
    this.notify();
    return student;
  }

  updateDocument(id, documentId, updates, user) {
    const student = this.documentService.updateDocument(id, documentId, updates, user);
    this.notify();
    return student;
  }

  archiveDocument(id, documentId, user) {
    const student = this.documentService.archiveDocument(id, documentId, user);
    this.notify();
    return student;
  }

  restoreDocument(id, documentId, user) {
    const student = this.documentService.restoreDocument(id, documentId, user);
    this.notify();
    return student;
  }

  deleteDocument(id, documentId, user) {
    const student = this.documentService.deleteDocument(id, documentId, user);
    this.notify();
    return student;
  }

  addNote(id, content, user) {
    const student = this.studentService.addNote(id, content, user);
    this.notify();
    return student;
  }

  addSupportReview(id, data, user) {
    const student = this.studentService.addSupportReview(id, data, user);
    this.notify();
    return student;
  }

  addIncidentReport(id, data, user) {
    const student = this.studentService.addIncidentReport(id, data, user);
    this.notify();
    return student;
  }

  addCertificate(id, data, user) {
    const student = this.studentService.addCertificate(id, data, user);
    this.notify();
    return student;
  }

  updateCertificate(id, certificateId, updates, user) {
    const student = this.studentService.updateCertificate(id, certificateId, updates, user);
    this.notify();
    return student;
  }

  archiveCertificate(id, certificateId, user) {
    const student = this.studentService.archiveCertificate(id, certificateId, user);
    this.notify();
    return student;
  }

  restoreCertificate(id, certificateId, user) {
    const student = this.studentService.restoreCertificate(id, certificateId, user);
    this.notify();
    return student;
  }

  deleteCertificate(id, certificateId, user) {
    const student = this.studentService.deleteCertificate(id, certificateId, user);
    this.notify();
    return student;
  }

  getAttendanceTrend(student) {
    return this.attendanceService.getAttendanceTrend(student);
  }

  shouldAutoFlag(student, threshold) {
    return this.attendanceService.shouldAutoFlag(student, threshold);
  }

  buildAttendanceTimeline(student) {
    return this.attendanceService.buildAttendanceTimeline(student);
  }

  getAuditLogs(studentId) {
    return this.auditService.getLogs(studentId);
  }
}
